/**
 * Function to handle API endpoint selection
 * Manages endpoint selection via URL parameters or cookies for both Laravel and Salesforce
 */
(() => {
	/**
	 * Set, or clear, an endpoint cookie
	 */
	const setCookie = (name: string, value?: string) => {
		const segments = {
			[name]: value || "",
			SameSite: "Strict",
			path: "/"
		}
		if (!value) {
			segments["Max-Age"] = "-99999999";
		}
		// This accounts for a remarkably annoying bug in Firefox
		if (window.location.hostname.endsWith("staging.plaudit.com")) {
			segments.domain = "staging.plaudit.com";
		}
		document.cookie = `${Object.entries(segments).map(([key, value]) => `${key}=${value}`).join("; ")}; Secure`;
	};

	/**
	 * Get a cookie value by name
	 */
	const getCookie = (name: string): string | undefined => {
		return `; ${document.cookie}`.split(`; ${name}=`)[1]?.split(';').shift();
	};

	// Wait for the DOM to be fully loaded before executing
	document.addEventListener('DOMContentLoaded', () => {
		// Find all endpoint picker forms in the document
		const forms = document.querySelectorAll<HTMLFormElement>(".endpoint-picker");
		for (const form of forms) {
			const dropdown = form.querySelector("select");
			if (!dropdown) {
				continue;
			}

			// 1) Determine the active endpoints
			let activeLaravelEndpoint: string | undefined;
			let activeSalesforceEndpoint: string | undefined;
			
			const potentialLaravelParameter = new URLSearchParams(location.search).get("laravel_endpoint");
			const potentialSalesforceParameter = new URLSearchParams(location.search).get("salesforce_endpoint");
			
			if (potentialLaravelParameter || potentialSalesforceParameter) {
				// Endpoints are specified in the URL query parameters
				if (potentialLaravelParameter) {
					activeLaravelEndpoint = potentialLaravelParameter;
					// Add https:// prefix if the endpoint doesn't have it
					if (!activeLaravelEndpoint.startsWith("http")) {
						activeLaravelEndpoint = `https://${activeLaravelEndpoint}`;
					}
					setCookie('laravel_endpoint', activeLaravelEndpoint);
				}
				
				if (potentialSalesforceParameter) {
					activeSalesforceEndpoint = potentialSalesforceParameter;
					// Add https:// prefix if the endpoint doesn't have it
					if (!activeSalesforceEndpoint.startsWith("http")) {
						activeSalesforceEndpoint = `https://${activeSalesforceEndpoint}`;
					}
					setCookie('salesforce_endpoint', activeSalesforceEndpoint);
				}

				// Redirect to cleaned URL. Remove the parameters from URL to avoid reapplying on page reload
				const loc = new URL(location.href);
				loc.searchParams.delete("laravel_endpoint");
				loc.searchParams.delete("salesforce_endpoint");
				window.location.href = loc.toString();

				return;
			} else {
				// If no URL parameters, check for endpoints in cookies
				activeLaravelEndpoint = getCookie('laravel_endpoint');
				activeSalesforceEndpoint = getCookie('salesforce_endpoint');
				
				if (!activeLaravelEndpoint && dropdown.value) {
					const selectedOption = dropdown.options[dropdown.selectedIndex];
					activeLaravelEndpoint = selectedOption.getAttribute('data-laravel') || dropdown.value;
					activeSalesforceEndpoint = selectedOption.getAttribute('data-salesforce') || '';
					setCookie('laravel_endpoint', activeLaravelEndpoint);
					if (activeSalesforceEndpoint) {
						setCookie('salesforce_endpoint', activeSalesforceEndpoint);
					}
				}
			}
			
			// 2) Select the active endpoint in the dropdown
			let activeEndpointIsValid = false;
			for (const option of dropdown.options) {
				const optionLaravel = option.getAttribute('data-laravel') || option.value;
				if (optionLaravel === activeLaravelEndpoint) {
					activeEndpointIsValid = true;
					if (!option.selected) {
						option.label += " (active)";
						option.selected = true;
					}
				} else if (option.selected) {
					option.selected = false;
				}
			}
			
			// Reset to default if the active endpoint isn't valid
			if (activeLaravelEndpoint && !activeEndpointIsValid) {
				const defaultOption = dropdown.querySelector<HTMLOptionElement>("option.default");
				if (defaultOption) {
					// Clear the cookies by setting negative Max-Age
					setCookie('laravel_endpoint');
					setCookie('salesforce_endpoint');
					window.location.reload();
				}
			}

			// 3) Set up endpoint change handler, which will set both cookies and reload the page
			dropdown.addEventListener('change', () => {
				const selectedOption = dropdown.options[dropdown.selectedIndex];
				const laravelEndpoint = selectedOption.getAttribute('data-laravel') || dropdown.value;
				const salesforceEndpoint = selectedOption.getAttribute('data-salesforce') || '';
				
				setCookie('laravel_endpoint', laravelEndpoint);
				if (salesforceEndpoint) {
					setCookie('salesforce_endpoint', salesforceEndpoint);
				} else {
					setCookie('salesforce_endpoint'); // Clear if empty
				}
				
				window.location.reload();
			});
		}
	});
})();

